import { useState } from 'react'
import { format, isValid } from 'date-fns'
import ShikiHighlighter from 'react-shiki'
import { CheckCircle, XCircle, Loader2, Play, RefreshCw, Clock, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DiffViewer } from '@/components/diff-viewer/DiffViewer'
import {
  useCodegenJobByPhenotype,
  useCodegenSSE,
  useTriggerCodegen,
  useTestResults,
} from '@/hooks/use-codegen'
import { type ExampleTestResult, type BuilderMode } from '@/api/codegen'

function safeFormat(value: string | number | undefined | null, fmt: string, fallback = '\u2014'): string {
  if (!value) return fallback
  const d = new Date(value)
  return isValid(d) ? format(d, fmt) : fallback
}

interface CodeGenTabProps {
  phenotypeId: string
}

export function CodeGenTab({ phenotypeId }: CodeGenTabProps) {
  const { data: job, isLoading } = useCodegenJobByPhenotype(phenotypeId)
  const { mutate: trigger, isPending: isTriggering } = useTriggerCodegen()

  // SSE: connect only when job is pending or running
  const isActive = job?.status === 'pending' || job?.status === 'running'
  const { events } = useCodegenSSE(job?.id, isActive)

  // Parse test results from completed job
  const testResults = useTestResults(job?.testResultsJson)

  // Builder mode selector
  const [builderMode, setBuilderMode] = useState<BuilderMode>('docker-builder')

  // Toggle state for inline DiffViewer — only one example expanded at a time
  const [expandedExampleId, setExpandedExampleId] = useState<string | null>(null)

  // --- Loading state ---
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="h-4 w-48 bg-muted animate-pulse rounded" />
            <div className="h-4 w-32 bg-muted animate-pulse rounded" />
          </div>
        </CardContent>
      </Card>
    )
  }

  // --- No job state ---
  if (!job) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground mb-4">
            No code generation jobs yet. Mark at least 3 examples to auto-trigger generation.
          </p>
          <div className="flex items-center gap-3">
            <select
              value={builderMode}
              onChange={(e) => setBuilderMode(e.target.value as BuilderMode)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="gemini">Gemini (LLM pipeline)</option>
              <option value="docker-builder">Docker Builder (Claude Code)</option>
            </select>
            <Button
              onClick={() => trigger({ phenotypeId, mode: builderMode })}
              disabled={isTriggering}
            >
              {isTriggering ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Trigger Code Generation
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // --- Job exists — render status, progress, code ---
  return (
    <div className="space-y-6">
      {/* Header: status + trigger button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold">Code Generation</h3>
          <StatusBadge status={job.status} />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={builderMode}
            onChange={(e) => setBuilderMode(e.target.value as BuilderMode)}
            className="rounded-md border border-input bg-background px-2 py-1 text-sm"
            disabled={isActive}
          >
            <option value="gemini">Gemini</option>
            <option value="docker-builder">Docker Builder</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => trigger({ phenotypeId, mode: builderMode })}
            disabled={isTriggering || isActive}
          >
            {isTriggering ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Re-trigger
          </Button>
        </div>
      </div>

      {/* Job metadata */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Job ID</p>
              <p className="font-mono text-xs truncate">{job.id}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Triggered By</p>
              <p>{job.triggeredBy || 'auto'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Started</p>
              <p>{safeFormat(job.startedAt, 'MMM d, HH:mm:ss')}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Completed</p>
              <p>{safeFormat(job.completedAt, 'MMM d, HH:mm:ss')}</p>
            </div>
            {job.builderMode && job.builderMode !== 'docker-builder' && (
              <div>
                <p className="text-xs text-muted-foreground">Builder</p>
                <Badge variant="secondary" className="text-xs">{job.builderMode}</Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Progress log (visible when active or recently completed) */}
      {(isActive || events.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pipeline Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {events.map((event, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <StepIcon step={event.step} />
                  <span className="font-medium">{formatStepName(event.step)}</span>
                  {event.message && (
                    <span className="text-muted-foreground">{event.message}</span>
                  )}
                  <span className="text-xs text-muted-foreground ml-auto">
                    {safeFormat(event.timestamp, 'HH:mm:ss')}
                  </span>
                </div>
              ))}
              {isActive && (
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Running...</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Failure reason */}
      {job.status === 'failed' && job.errorMessage && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Generation Failed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm font-mono whitespace-pre-wrap">{job.errorMessage}</p>
          </CardContent>
        </Card>
      )}

      {/* Per-example test results (VIS-03 / VIS-04 / VIS-05) */}
      {(job.status === 'completed' || job.status === 'failed') && testResults.length > 0 && (
        <TestResultsSection
          testResults={testResults}
          expandedExampleId={expandedExampleId}
          onToggleExpand={(fileId) =>
            setExpandedExampleId(expandedExampleId === fileId ? null : fileId)
          }
        />
      )}

      {/* Generated code viewer (VIS-02) */}
      {job.status === 'completed' && job.extractorCode && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Generated Extractor</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-hidden">
            <div className="overflow-auto max-h-[600px] rounded-b-md text-sm">
              <ShikiHighlighter language="typescript" theme="github-dark">
                {job.extractorCode}
              </ShikiHighlighter>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// --- TestResultsSection ---

function TestResultsSection({
  testResults,
  expandedExampleId,
  onToggleExpand,
}: {
  testResults: ExampleTestResult[]
  expandedExampleId: string | null
  onToggleExpand: (fileId: string) => void
}) {
  if (testResults.length === 0) return null

  const passCount = testResults.filter(r => r.passed).length
  const failCount = testResults.length - passCount

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>Test Results</span>
          <span className="text-sm font-normal text-muted-foreground">
            {passCount} passed, {failCount} failed &mdash; {testResults.length} total
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {testResults.map((result) => (
            <div key={result.fileId} className="space-y-2">
              {/* Per-example row */}
              <div className="flex items-center justify-between py-2 px-3 rounded-md bg-muted/50">
                <div className="flex items-center gap-3">
                  {result.passed ? (
                    <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-destructive shrink-0" />
                  )}
                  <span className="font-mono text-sm truncate max-w-[300px]">
                    {result.fileId}
                  </span>
                  <Badge variant={result.passed ? 'outline' : 'destructive'} className="text-xs">
                    {result.passed ? 'Pass' : 'Fail'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  {result.error && (
                    <span className="text-xs text-destructive max-w-[200px] truncate">
                      {result.error}
                    </span>
                  )}
                  {!result.passed && result.expected != null && result.actual != null && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onToggleExpand(result.fileId)}
                      className="text-xs"
                    >
                      {expandedExampleId === result.fileId ? (
                        <>
                          <ChevronDown className="mr-1 h-3 w-3" />
                          Hide Diff
                        </>
                      ) : (
                        <>
                          <ChevronRight className="mr-1 h-3 w-3" />
                          View Diff
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Inline DiffViewer (expanded) */}
              {expandedExampleId === result.fileId && !result.passed && (
                <div className="ml-7">
                  <DiffViewer
                    expected={result.expected}
                    actual={result.actual}
                    viewMode="split"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

// --- Helper components ---

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
    pending: { variant: 'secondary', label: 'Pending' },
    running: { variant: 'default', label: 'Running' },
    completed: { variant: 'outline', label: 'Completed' },
    failed: { variant: 'destructive', label: 'Failed' },
  }
  const { variant, label } = variants[status] || { variant: 'secondary' as const, label: status }
  return <Badge variant={variant}>{label}</Badge>
}

function StepIcon({ step }: { step: string }) {
  if (step === 'done') return <CheckCircle className="h-4 w-4 text-green-500" />
  if (step === 'failed') return <XCircle className="h-4 w-4 text-destructive" />
  if (step === 'init') return <Clock className="h-4 w-4 text-blue-500" />
  return <CheckCircle className="h-4 w-4 text-muted-foreground" />
}

function formatStepName(step: string): string {
  const names: Record<string, string> = {
    init: 'Initializing',
    structure_analysis: 'Structure Analysis',
    logic_extraction: 'Logic Extraction',
    pattern_consensus: 'Pattern Consensus',
    code_generation: 'Code Generation',
    test_generation: 'Test Generation',
    sandbox_validation: 'Sandbox Validation',
    docker_builder: 'Docker Builder',
    agent_log: 'Agent',
    test_execution: 'Test Execution',
    refinement: 'Refinement',
    done: 'Complete',
    failed: 'Failed',
  }
  return names[step] || step
}
